import {focusElement, hookToAttributeSelector} from '@wix/panda-js-utils'
import {detailsPageDataHooks as DH} from '@wix/wix-events-data-hooks'
import {useEnvironment, useTranslation} from '@wix/yoshi-flow-editor'
import classNames from 'classnames'
import React, {useEffect} from 'react'
import {DropdownOptionProps} from 'wix-ui-tpa/cssVars'
import {useCurrencyFormatter} from '../../../../../../../../commons/hooks/currency'
import {useAppActions, useAppSelector} from '../../../../../hooks/runtime'
import {
  DEFAULT_OPTION_ID,
  getPriceOptions,
  getSelectedPriceOptionId,
  getSelectedPriceValue,
  getSelectedZoneOptionId,
  getSelectedZoneValue,
  getZoneOptions,
} from '../../../../../selectors/seating/filters'
import {isInAccessibilityMode} from '../../../../../selectors/seating/mode'
import {getSeatingPlan} from '../../../../../selectors/seating/plan'
import {getTickets} from '../../../../../selectors/tickets'
import {FilterContainer} from './filter-container'
import s from './filters.scss'
import {StyledFloatingDropdown} from './styled-dropdown'
import {VerticalDivider} from './vertical-divider'

export const Filters = () => {
  const {t} = useTranslation()
  const {isMobile} = useEnvironment()
  const {unselectPlace, selectPrice, selectLocation} = useAppActions()
  const {getTicketPriceText, getFormattedMoney} = useCurrencyFormatter()

  const seatingPlan = useAppSelector(getSeatingPlan)
  const showAccessibilityMode = useAppSelector(isInAccessibilityMode)
  const priceOptions = useAppSelector(state =>
    getPriceOptions(getTickets(state), t, {getTicketPriceText, getFormattedMoney}),
  )
  const selectedPriceOptionId = useAppSelector(state => getSelectedPriceOptionId(state, priceOptions))

  const zoneOptions = getZoneOptions(seatingPlan, t)

  const selectedZoneOptionId = useAppSelector(state => getSelectedZoneOptionId(state, zoneOptions))

  const selectedPriceValue = getSelectedPriceValue(priceOptions, selectedPriceOptionId)
  const selectedZoneValue = getSelectedZoneValue(zoneOptions, selectedZoneOptionId)

  useEffect(() => {
    if (showAccessibilityMode) {
      selectPrice(null)
      selectLocation(null)
    }
  }, [showAccessibilityMode, selectPrice, selectLocation])

  useEffect(() => {
    if (showAccessibilityMode) {
      unselectPlace()
    }
  }, [selectedZoneOptionId, selectedPriceOptionId])

  const handleSelectPrice = (selectedOption: DropdownOptionProps) => {
    selectPrice(selectedOption.id === DEFAULT_OPTION_ID ? null : selectedOption.id)
    focusElement({selector: hookToAttributeSelector(DH.SEATING_PRICE_FILTER)})
  }

  const handleSelectZone = (selectedOption: DropdownOptionProps) => {
    selectLocation(selectedOption.id === DEFAULT_OPTION_ID ? null : selectedOption.id)
    focusElement({selector: hookToAttributeSelector(DH.SEATING_ZONE_FILTER)})
  }

  return (
    <div className={classNames(s.container, {[s.mobileContainer]: isMobile})}>
      <FilterContainer>
        <StyledFloatingDropdown
          dataHook={DH.SEATING_PRICE_FILTER}
          label={t('seatings_filters_priceLabel')}
          value={selectedPriceOptionId}
          ariaLabel={t('seatings_filters_priceAriaLabel', {value: selectedPriceValue})}
          options={priceOptions}
          onChange={handleSelectPrice}
        />
      </FilterContainer>
      {isMobile ? null : <VerticalDivider className={s.divider} />}
      <FilterContainer>
        <StyledFloatingDropdown
          dataHook={DH.SEATING_ZONE_FILTER}
          value={selectedZoneOptionId}
          label={t('seatings_filters_zoneLabel')}
          ariaLabel={t('seatings_filters_zoneAriaLabel', {value: selectedZoneValue})}
          options={zoneOptions}
          onChange={handleSelectZone}
        />
      </FilterContainer>
    </div>
  )
}
